<script lang="ts" context="module">
  const deg2rad = (deg: number) => (deg * Math.PI) / 180

  const R = 167
  const MAX_ROTATION = 45 /* deg */
</script>

<script lang="ts">
  import { onMount } from "svelte"
  import DirectionIndicator from "../DirectionIndicator.svelte"

  export let rotation = 0
  export let direction: "left" | "right" | undefined = undefined

  let container: HTMLDivElement
  let width = 0

  onMount(() => {
    const observer = new ResizeObserver(([entry]) => (width = entry.contentRect.width))

    observer.observe(container)

    return () => observer.disconnect()
  })

  $: a = (90 / MAX_ROTATION) * Math.max(-MAX_ROTATION, Math.min(rotation, MAX_ROTATION))
  $: rx = R * Math.sign(a) * Math.sin(deg2rad(a))
  $: sweep = Math.sign(a) < 0 ? 1 : 0
</script>

<div bind:this={container} class={`direction-${direction}`} style:--a={a} style:--r={width / 2}>
  <svg
    class="guide"
    width="336"
    height="389"
    viewBox="0 0 336 389"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d={`M168,1 A${rx},193 0 0,${sweep} 168,388`}
      stroke-width="1.75"
      stroke-linecap="round"
      fill="none"
    />
  </svg>

  {#if direction}
    {#key direction}
      <DirectionIndicator />
    {/key}
  {/if}
</div>

<style lang="postcss">
  div {
    @apply relative aspect-[336/389] h-full w-auto;
  }

  .guide {
    @apply h-full w-full stroke-current drop-shadow-[0px_0px_6px_currentColor];

    mask: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 1),
      rgba(0, 0, 0, 0) 41.6%,
      rgba(0, 0, 0, 0) 58.4%,
      rgba(0, 0, 0, 1)
    );

    & > path {
      @apply animate-[0.75s_ease-out_1_forwards_fill];

      stroke-dasharray: 195 390;
      stroke-dashoffset: 195;

      @keyframes fill {
        0% {
          stroke-dasharray: 195 390;
          stroke-dashoffset: 195;
        }
        100% {
          stroke-dasharray: 195 0;
          stroke-dashoffset: 0;
        }
      }
    }
  }

  div > :global(*:last-child):not(.guide) {
    @apply absolute left-1/2 top-1/2 h-auto w-[14.67%] stroke-current drop-shadow-[0px_0px_3px_currentColor];

    transform-origin: center center calc(-1px * var(--r));
    transform: translateX(-50%) translateY(-50%) rotateY(calc(-1deg * var(--a)));
  }

  div.direction-left > :global(*:last-child):not(.guide) {
    transform: translateX(-50%) translateY(-50%) rotateY(calc(-1deg * var(--a))) rotateZ(180deg);
  }
</style>
